/**
 * Reusable skeleton components for loading states across the app.
 * Uses the shimmer animation defined in tailwind.config.js
 */

import { useState } from "react";

// Base shimmer block
export const Shimmer = ({ className = "" }) => (
  <div className={`animate-pulse rounded-2xl bg-gradient-to-r from-sand-200 via-sand-100 to-sand-200 bg-[length:800px_100%] ${className}`} />
);

// Image with skeleton loading — shows shimmer until image loads
export const SkeletonImage = ({ src, alt, className = "", imgClassName = "", style, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (error || !src) {
    return (
      <div className={`flex items-center justify-center bg-sand-100 ${className}`} style={style}>
        <svg className="h-8 w-8 text-sand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`} style={style} onClick={onClick}>
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-sand-200 via-sand-100 to-sand-200 bg-[length:800px_100%]" />
      )}
      <img
        src={src}
        alt={alt || ""}
        className={`${imgClassName} transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        loading="lazy"
      />
    </div>
  );
};

// Profile card skeleton (grid view)
export const ProfileCardGridSkeleton = () => (
  <div className="overflow-hidden rounded-3xl border border-sand-200 bg-white">
    <Shimmer className="aspect-[4/5] w-full rounded-none" />
    <div className="space-y-3 p-4">
      <Shimmer className="h-5 w-3/4" />
      <Shimmer className="h-3 w-1/2" />
      <div className="flex gap-2">
        <Shimmer className="h-6 w-20 rounded-full" />
        <Shimmer className="h-6 w-16 rounded-full" />
      </div>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <Shimmer className="h-10" />
        <Shimmer className="h-10" />
      </div>
    </div>
  </div>
);

// Profile card skeleton (list view)
export const ProfileCardListSkeleton = () => (
  <div className="overflow-hidden rounded-3xl border border-sand-200 bg-white p-4 sm:p-5">
    <div className="flex items-center gap-4">
      <Shimmer className="h-[72px] w-[72px] shrink-0 rounded-2xl" />
      <div className="flex-1 space-y-2">
        <Shimmer className="h-5 w-2/3" />
        <Shimmer className="h-3 w-full" />
        <Shimmer className="h-3 w-1/2" />
      </div>
      <div className="hidden sm:flex flex-col gap-2">
        <Shimmer className="h-9 w-20" />
        <Shimmer className="h-9 w-20" />
      </div>
    </div>
  </div>
);

// Dashboard hero skeleton
export const DashboardHeroSkeleton = () => (
  <div className="rounded-[32px] bg-gradient-to-r from-brand-600/20 to-brand-700/10 p-5 sm:p-8">
    <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
      <div className="max-w-2xl space-y-4">
        <Shimmer className="h-6 w-48 rounded-full" />
        <Shimmer className="h-10 w-72" />
        <Shimmer className="h-4 w-64" />
        <div className="flex gap-3">
          <Shimmer className="h-11 w-32" />
          <Shimmer className="h-11 w-28" />
        </div>
      </div>
      <Shimmer className="h-36 w-36 rounded-[28px] sm:h-44 sm:w-44" />
    </div>
  </div>
);

// Stats row skeleton
export const StatsRowSkeleton = ({ count = 4 }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="card p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <Shimmer className="h-3 w-20" />
            <Shimmer className="h-8 w-16" />
            <Shimmer className="h-3 w-24" />
          </div>
          <Shimmer className="h-10 w-10 rounded-xl" />
        </div>
      </div>
    ))}
  </div>
);

// Chat list skeleton
export const ChatListSkeleton = () => (
  <div className="space-y-2 p-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 rounded-2xl p-2.5">
        <Shimmer className="h-12 w-12 rounded-full" />
        <div className="flex-1 space-y-2">
          <Shimmer className="h-4 w-2/3" />
          <Shimmer className="h-3 w-3/4" />
        </div>
        <Shimmer className="h-3 w-10" />
      </div>
    ))}
  </div>
);

// Messages skeleton
export const MessagesSkeleton = () => (
  <div className="flex-1 space-y-4 p-5">
    <Shimmer className="h-14 w-56 rounded-2xl" />
    <div className="ml-auto"><Shimmer className="h-12 w-48 rounded-2xl" /></div>
    <Shimmer className="h-16 w-64 rounded-2xl" />
    <div className="ml-auto"><Shimmer className="h-10 w-36 rounded-2xl" /></div>
    <Shimmer className="h-12 w-52 rounded-2xl" />
  </div>
);

// Settings page skeleton
export const SettingsSkeleton = () => (
  <div className="space-y-4">
    <div className="card-elevated p-5">
      <div className="flex items-center gap-4">
        <Shimmer className="h-16 w-16 rounded-2xl" />
        <div className="flex-1 space-y-2">
          <Shimmer className="h-5 w-40" />
          <Shimmer className="h-3 w-28" />
        </div>
      </div>
    </div>
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="rounded-3xl border border-sand-200 bg-white overflow-hidden">
        {Array.from({ length: 3 }).map((__, j) => (
          <div key={j} className="flex items-center gap-4 border-b border-sand-100 px-4 py-3.5 last:border-0">
            <Shimmer className="h-10 w-10 rounded-xl" />
            <div className="flex-1 space-y-1.5">
              <Shimmer className="h-4 w-32" />
              <Shimmer className="h-3 w-48" />
            </div>
            <Shimmer className="h-4 w-4 rounded" />
          </div>
        ))}
      </div>
    ))}
  </div>
);

// Profile detail skeleton
export const ProfileDetailSkeleton = () => (
  <div className="grid gap-6 lg:grid-cols-[460px_minmax(0,1fr)]">
    <div className="space-y-4">
      <Shimmer className="aspect-[4/5] w-full rounded-[28px]" />
      <div className="grid grid-cols-5 gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Shimmer key={i} className="aspect-square rounded-xl" />
        ))}
      </div>
      <div className="card p-5 space-y-3">
        <Shimmer className="h-3 w-16" />
        <Shimmer className="h-6 w-48" />
        <Shimmer className="h-11 w-full" />
        <Shimmer className="h-11 w-full" />
        <Shimmer className="h-11 w-full" />
      </div>
    </div>
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="card flex items-center gap-3 p-3">
            <Shimmer className="h-10 w-10 rounded-xl" />
            <div className="flex-1 space-y-1.5">
              <Shimmer className="h-2.5 w-12" />
              <Shimmer className="h-4 w-20" />
            </div>
          </div>
        ))}
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="card p-5 space-y-4">
          <div className="flex items-center gap-3">
            <Shimmer className="h-10 w-10 rounded-xl" />
            <Shimmer className="h-5 w-32" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {Array.from({ length: 6 }).map((__, j) => (
              <div key={j} className="space-y-1.5">
                <Shimmer className="h-2.5 w-16" />
                <Shimmer className="h-4 w-28" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Form skeleton (for CreateProfile / edit)
export const FormSkeleton = () => (
  <div className="space-y-5">
    <div className="rounded-3xl bg-brand-600/10 p-4 sm:p-7">
      <div className="space-y-3">
        <Shimmer className="h-5 w-32 rounded-full" />
        <Shimmer className="h-8 w-64" />
        <Shimmer className="h-3 w-48" />
        <Shimmer className="h-1.5 w-full rounded-full" />
      </div>
    </div>
    <div className="flex gap-2 overflow-hidden">
      {Array.from({ length: 7 }).map((_, i) => (
        <Shimmer key={i} className="h-9 w-24 shrink-0 rounded-2xl" />
      ))}
    </div>
    <div className="card-elevated p-5 sm:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Shimmer className="h-11 w-11 rounded-2xl" />
        <div className="space-y-1.5">
          <Shimmer className="h-3 w-12" />
          <Shimmer className="h-6 w-32" />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Shimmer className="h-3 w-20" />
            <Shimmer className="h-12 w-full" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Page transition wrapper
export const PageTransition = ({ children }) => (
  <div className="animate-fade-up">
    {children}
  </div>
);

// Full page loader (centered spinner)
export const FullPageLoader = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <div className="text-center">
      <div className="mx-auto h-10 w-10 animate-spin rounded-full border-3 border-sand-200 border-t-brand-600" />
      <p className="mt-4 text-[13px] font-semibold text-ink-400">Loading...</p>
    </div>
  </div>
);

// Inline loader (small, for buttons/sections)
export const InlineLoader = ({ text = "Loading..." }) => (
  <div className="flex items-center justify-center gap-2 py-8">
    <div className="h-5 w-5 animate-spin rounded-full border-2 border-sand-200 border-t-brand-600" />
    <span className="text-[13px] font-semibold text-ink-400">{text}</span>
  </div>
);
